import { Link, isRouteErrorResponse, useRouteError } from "react-router";
import { ApiError } from "@repo/api-client";
import { PageTitle } from "@/components/brand";
import { Button } from "@/components/ui/button";

/**
 * The catch-all route's page, and the error element of every page inside the
 * layout: Caddy serves index.html for any unknown path, so a mistyped URL
 * arrives here rather than at a server 404, and so does a space or an event
 * the api answers 404 for — which is also what a private one somebody is not
 * in looks like, so it never says which. Anything else is a crash, and says
 * so rather than claiming the page does not exist.
 */
export default function NotFound() {
  const error = useRouteError();
  // The catch-all route renders this with no error at all: null.
  const missing =
    error == null ||
    (isRouteErrorResponse(error) && error.status === 404) ||
    (error instanceof ApiError && error.status === 404);

  if (!missing) {
    return (
      <section className="space-y-4">
        <PageTitle of={["Something went wrong"]} />
        <h1 className="page-title">Something went wrong</h1>
        <p className="text-muted-foreground">This page could not be loaded. Trying again usually fixes it.</p>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button type="button" size="lg" onClick={() => window.location.reload()}>
            Try again
          </Button>
          <Button asChild variant="ghost" size="lg">
            <Link to="/">Back home</Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <PageTitle of={["Page not found"]} />
      <p aria-hidden="true" className="font-heading text-[7rem] leading-none font-semibold tracking-tighter text-foreground">
        404
      </p>
      <h1 className="page-title">Page not found</h1>
      <p className="text-muted-foreground">It may have moved, or you may not have access to it.</p>
      <div className="pt-2">
        <Button asChild size="lg">
          <Link to="/">Back home</Link>
        </Button>
      </div>
    </section>
  );
}

/**
 * The root's own error element, for when the layout itself fails — the
 * session could not be read — so there is no layout to draw inside. It keeps
 * the page's margins and says the same as a crash below it.
 */
export function RootError() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main className="mx-auto max-w-3xl px-4 pb-10 pt-[calc(2.5rem+env(safe-area-inset-top))]">
        <NotFound />
      </main>
    </div>
  );
}
